import { FC, useMemo, useState } from 'react';
import { TimeField } from 'utils/validators';
import { TimeInputVariant } from '.';

interface TimeDurationColumnProps {
  field: TimeField;
  id: string;
  value: string;
  label: string;
  disabled: boolean;
  inputBaseClasses: string;
  labelClassName: string;
  error: {
    isInvalid: boolean;
    message: string;
  };
  onChange: (field: TimeField, value: string) => void;
  onFocus: (field: TimeField) => void;
  showErrors: boolean;
  variant: TimeInputVariant;
  maxLength?: number;
}

const TimeDurationColumn: FC<TimeDurationColumnProps> = ({
  field,
  id,
  value,
  label,
  disabled,
  inputBaseClasses,
  labelClassName,
  error,
  onChange,
  onFocus,
  showErrors,
  variant,
  maxLength
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const borderClasses = useMemo(() => {
    if (error.isInvalid || isFocused) {
      if (variant === 'primary') {
        return `${error.isInvalid ? 'border-red-500' : 'border-primary-500'} `;
      }
      return `${
        error.isInvalid
          ? 'border-red-500'
          : 'border-gray-300 dark:border-secondary-dark-500'
      } `;
    }
    return 'border-transparent';
  }, [isFocused, error.isInvalid, variant]);

  return (
    <label
      className={`flex flex-col items-center justify-center border-2 rounded-lg ${borderClasses}`}
      htmlFor={id}>
      <span className={labelClassName}>{label}</span>
      <input
        id={id}
        type='number'
        value={value}
        disabled={disabled}
        max={maxLength}
        onChange={(event) => onChange(field, event.currentTarget.value)}
        onFocus={() => {
          setIsFocused(true);
          onFocus(field);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        className={inputBaseClasses}
        aria-label={label}
        aria-invalid={error.isInvalid}
        aria-describedby={error.message}
      />
      {showErrors && error.isInvalid && (
        <small className='text-red-600 ~text-[0.65rem]/xs animate-fadeIn'>
          {error.message}
        </small>
      )}
    </label>
  );
};

export default TimeDurationColumn;
